import { useEffect, useMemo, useState } from 'react'
import type { RepositoryMappingAggregateType } from '@cms/contracts'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { getRepositoryMapping, setRepositoryMapping } from '../api/endpoints.js'
import { parseVersionConflict, type VersionConflictInfo } from '../domain/conflict.js'
import {
  emptyRepositoryMappingDraft,
  repositoryMappingDraft,
  repositoryMappingErrors,
  repositoryMappingValue,
  sameRepositoryMappingDraft,
  type RepositoryMappingDraft,
} from '../domain/repository-mapping.js'
import { useI18n } from '../i18n/context.js'
import { resolveReason } from '../domain/reason.js'
import { Checkbox, ReasonNote, SelectField, Switch, TextField } from '../ui/fields.js'
import { Banner, Button, Icon, SkeletonLines } from '../ui/primitives.js'
import { ConflictDialog } from './ConflictDialog.js'

interface RepositoryMappingPanelProps<T extends RepositoryMappingAggregateType> {
  aggregateType: T
  resourceId: string
  resourceVersion: number
  canWrite: boolean
  primaryDirty: boolean
  onDirtyChange: (dirty: boolean) => void
  onExpandedChange?: (expanded: boolean) => void
  onVersionChange: (version: number) => void
}

export function RepositoryMappingPanel<T extends RepositoryMappingAggregateType>({
  aggregateType,
  resourceId,
  resourceVersion,
  canWrite,
  primaryDirty,
  onDirtyChange,
  onExpandedChange,
  onVersionChange,
}: RepositoryMappingPanelProps<T>) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [version, setVersion] = useState(0)
  const [original, setOriginal] = useState<RepositoryMappingDraft>(() => emptyRepositoryMappingDraft())
  const [draft, setDraft] = useState<RepositoryMappingDraft>(() => emptyRepositoryMappingDraft())
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [conflict, setConflict] = useState<VersionConflictInfo | null>(null)
  const dirty = loaded && !sameRepositoryMappingDraft(original, draft)
  const editingDisabled = busy || primaryDirty
  const fallbackReason = t('reason.repositoryMapping')
  const validationErrors = useMemo(() => repositoryMappingErrors(aggregateType, draft), [aggregateType, draft])

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange])
  useEffect(() => {
    if (loaded && !dirty && !primaryDirty) setVersion(resourceVersion)
  }, [dirty, loaded, primaryDirty, resourceVersion])
  useEffect(() => {
    setOpen(false); setLoaded(false); setUnavailable(false); setLoading(false); setBusy(false); setVersion(0)
    const empty = emptyRepositoryMappingDraft()
    setOriginal(empty); setDraft(empty); setNote(''); setError(null); setMessage(null); setConflict(null)
    onDirtyChange(false)
    onExpandedChange?.(false)
    return () => onExpandedChange?.(false)
  }, [aggregateType, resourceId, onDirtyChange, onExpandedChange])

  const load = async () => {
    if (!resourceId || unavailable) return
    setLoading(true); setError(null); setMessage(null)
    try {
      const detail = await getRepositoryMapping(aggregateType, resourceId)
      const next = repositoryMappingDraft(aggregateType, detail.repositoryMapping)
      setOriginal(next); setDraft(next); setVersion(detail.version); setLoaded(true); setConflict(null)
      if (!primaryDirty) onVersionChange(detail.version)
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 503)) {
        setUnavailable(true); setError(null)
      } else setError(err instanceof ApiError ? err.message : t('errors.network'))
    } finally { setLoading(false) }
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    onExpandedChange?.(next)
    if (next && !loaded && !unavailable && !loading) void load()
  }

  const save = async (expectedVersion = version) => {
    if (!canWrite || primaryDirty || !dirty || validationErrors.length) return
    setBusy(true); setError(null); setMessage(null)
    try {
      await setRepositoryMapping(aggregateType, resourceId, {
        mapping: repositoryMappingValue(aggregateType, draft),
        reason: resolveReason(note, fallbackReason),
        expectedVersion,
      }, newIdempotencyKey())
      const detail = await getRepositoryMapping(aggregateType, resourceId)
      const next = repositoryMappingDraft(aggregateType, detail.repositoryMapping)
      setOriginal(next); setDraft(next); setVersion(detail.version); setNote(''); setConflict(null)
      onVersionChange(detail.version); setMessage(t('repositoryMapping.saved'))
    } catch (err) {
      const info = parseVersionConflict(err)
      if (info) setConflict(info)
      else if (err instanceof ApiError && (err.status === 404 || err.status === 503)) {
        setUnavailable(true); setError(null)
      } else setError(err instanceof ApiError ? err.message : t('errors.network'))
    } finally { setBusy(false) }
  }

  if (!canWrite || !resourceId) return null

  const patchBody = (index: number, change: Partial<RepositoryMappingDraft['sourceBodies'][number]>) =>
    setDraft((current) => ({ ...current, sourceBodies: current.sourceBodies.map((entry, position) => position === index ? { ...entry, ...change } : entry) }))

  return <section className="card repository-mapping-panel" data-testid="repository-mapping-panel">
    <button type="button" className="disclosure" aria-expanded={open} onClick={toggle}>
      <Icon name="code"/>
      <span className="disclosure-text"><strong>{t('repositoryMapping.title')}</strong><small>{t('repositoryMapping.summary')}</small></span>
      <Icon name={open ? 'expand_less' : 'expand_more'}/>
    </button>
    {open && <div className="disclosure-body form-stack">
      <Banner tone="info" title={t('repositoryMapping.warningTitle')}>{t('repositoryMapping.warning')}</Banner>
      {loading ? <SkeletonLines count={3}/> : unavailable ? <Banner tone="info">{t('repositoryMapping.unavailable')}</Banner> : loaded ? <>
        {error && <Banner tone="error">{error}</Banner>}
        {message && <Banner tone="success">{message}</Banner>}
        {primaryDirty && <Banner tone="info">{t('repositoryMapping.primaryDirty')}</Banner>}
        <Switch checked={draft.enabled} disabled={editingDisabled} onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))} label={t('repositoryMapping.enabled')} supporting={t('repositoryMapping.enabledHelp')}/>
        {draft.enabled && <div className="form-stack">
          <TextField label={t('repositoryMapping.metadataPath')} mono value={draft.metadataPath} disabled={editingDisabled} onChange={(event) => setDraft((current) => ({ ...current, metadataPath: event.target.value }))} placeholder={t('repositoryMapping.pathPlaceholder')}/>
          <Checkbox checked={draft.metadataFinalNewline} disabled={editingDisabled} onChange={(event) => setDraft((current) => ({ ...current, metadataFinalNewline: event.target.checked }))} label={t('repositoryMapping.finalNewline')}/>
          {aggregateType === 'Content' && <div className="form-stack">
            <div className="section-row"><h3 className="section-title">{t('repositoryMapping.sourceBodies')}</h3><Button variant="tonal" icon="add" disabled={editingDisabled} onClick={() => setDraft((current) => ({ ...current, sourceBodies: [...current.sourceBodies, { repositoryPath: '', finalNewline: true, format: 'markdown', dataPath: '' }] }))}>{t('repositoryMapping.addBody')}</Button></div>
            {draft.sourceBodies.length ? draft.sourceBodies.map((body, index) => <div className="list-item" key={index}>
              <div className="form-grid">
                <TextField label={t('repositoryMapping.repositoryPath')} mono value={body.repositoryPath} disabled={editingDisabled} onChange={(event) => patchBody(index, { repositoryPath: event.target.value })}/>
                <TextField label={t('repositoryMapping.dataPath')} mono value={body.dataPath} disabled={editingDisabled} onChange={(event) => patchBody(index, { dataPath: event.target.value })}/>
                <SelectField label={t('repositoryMapping.format')} value={body.format} disabled={editingDisabled} onChange={(event) => patchBody(index, { format: event.target.value === 'html' ? 'html' : 'markdown' })}><option value="markdown">Markdown</option><option value="html">HTML</option></SelectField>
              </div>
              <div className="section-row">
                <Checkbox checked={body.finalNewline} disabled={editingDisabled} onChange={(event) => patchBody(index, { finalNewline: event.target.checked })} label={t('repositoryMapping.finalNewline')}/>
                <Button variant="danger-text" icon="delete" disabled={editingDisabled} onClick={() => setDraft((current) => ({ ...current, sourceBodies: current.sourceBodies.filter((_, position) => position !== index) }))}>{t('common.remove')}</Button>
              </div>
            </div>) : <p className="muted">{t('repositoryMapping.noBodies')}</p>}
          </div>}
          {aggregateType === 'Theme' && <div className="form-stack">
            <Switch checked={draft.authoredCssEnabled} disabled={editingDisabled} onChange={(event) => setDraft((current) => ({ ...current, authoredCssEnabled: event.target.checked }))} label={t('repositoryMapping.authoredCss')} supporting={t('repositoryMapping.authoredCssHelp')}/>
            {draft.authoredCssEnabled && <>
              <TextField label={t('repositoryMapping.repositoryPath')} mono value={draft.authoredCssPath} disabled={editingDisabled} onChange={(event) => setDraft((current) => ({ ...current, authoredCssPath: event.target.value }))}/>
              <Checkbox checked={draft.authoredCssFinalNewline} disabled={editingDisabled} onChange={(event) => setDraft((current) => ({ ...current, authoredCssFinalNewline: event.target.checked }))} label={t('repositoryMapping.finalNewline')}/>
            </>}
          </div>}
        </div>}
        {validationErrors.length > 0 && <Banner tone="error">{t('repositoryMapping.requiredPaths')}</Banner>}
        <div className="inline-savebar">
          <ReasonNote value={note} onChange={setNote} fallback={fallbackReason} disabled={busy}/>
          <span className="save-state">{dirty ? t('common.unsaved') : t('common.saved')}</span>
          <Button variant="filled" icon="check" disabled={busy || primaryDirty || !dirty || validationErrors.length > 0} onClick={() => void save()}>{busy ? t('common.saving') : t('repositoryMapping.save')}</Button>
        </div>
      </> : error ? <Banner tone="error">{error}</Banner> : null}
    </div>}
    {conflict && <ConflictDialog
      conflict={conflict}
      busy={busy}
      title={t('repositoryMapping.conflictTitle')}
      message={t('repositoryMapping.conflictMessage', { actualVersion: conflict.actualVersion })}
      onKeepMine={() => void save(conflict.actualVersion)}
      onKeepTheirs={() => void load()}
      onDismiss={() => setConflict(null)}
    />}
  </section>
}
